import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldIcon, Trash2Icon, PlusIcon, BuildingIcon, UserPlusIcon, BriefcaseIcon, EditIcon, PaperclipIcon, FlaskConicalIcon, EyeIcon, FileTextIcon, PresentationIcon, TruckIcon, LinkIcon, RefreshCwIcon, CheckIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ClienteForm from "@/components/ClienteForm";
import { FornecedoresAdmin } from "@/components/FornecedoresAdmin";
import { useAuth } from "@/context/AuthContext";
import { useActiveBase } from "@/context/BaseContext";
import { useNavigate } from "react-router-dom";

export default function Admin() {
  const { user: currentUser } = useAuth();
  const { setActiveBase } = useActiveBase();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [activeTab, setActiveTab] = useState(isAdmin ? "bases" : "users");
  const [bases, setBases] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clientesWMS, setClientesWMS] = useState([]);
  const [products, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ codigo: "", nome: "", funcao_quimica: "", densidade: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "operator", base: "", modules: [] });
  const [editingBase, setEditingBase] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", code: "", region: "", razao_social: "", cnpj: "", endereco: "" });
  const [baseOps, setBaseOps] = useState(null);
  const [baseOpsData, setBaseOpsData] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "", role: "operator", base: "", password: "" });
  const [editingCliente, setEditingCliente] = useState(null);
  const [editClienteForm, setEditClienteForm] = useState({ nome: "", cnpj: "", cidade: "", estado: "" });

  const load = async () => {
    const usersEndpoint = isAdmin ? "/admin/users" : "/manager/users";
    const [b, u, a, c, cw, p] = await Promise.all([
      api.get("/bases").catch(() => ({ data: [] })),
      api.get(usersEndpoint),
      isAdmin ? api.get("/audit/log").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get("/clientes", { params: { tipo: "destino" } }).catch(() => ({ data: [] })),
      isAdmin ? api.get("/clientes", { params: { tipo: "wms" } }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get("/products").catch(() => ({ data: [] })),
    ]);
    setBases(b.data);
    setUsers(u.data);
    setAuditLog(a.data);
    setClientes(c.data);
    setClientesWMS(cw.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (baseId, value) => {
    if (!value && !window.confirm("Desligar esta base também desativa todos os módulos. Continuar?")) return;
    try {
      await api.patch(`/admin/bases/${baseId}/active`, { active: value });
      toast.success(value ? "Base ativada" : "Base desativada — usuários vinculados serão bloqueados");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleModule = async (baseId, key, value) => {
    try {
      await api.patch(`/admin/bases/${baseId}/modules`, { [key]: value });
      toast.success("Atualizado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) return toast.error("Preencha email/senha/nome");
    try {
      const endpoint = isAdmin ? "/admin/users" : "/manager/users";
      const payload = isAdmin ? newUser : { email: newUser.email, password: newUser.password, name: newUser.name, role: newUser.role, modules: newUser.modules };
      await api.post(endpoint, { ...payload, modules: newUser.modules?.length ? newUser.modules : null });
      toast.success("Usuário criado");
      setNewUser({ email: "", password: "", name: "", role: "operator", base: "", modules: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const MODULOS_USUARIO = [
    { key: "portaria", label: "Portaria" },
    { key: "balanca", label: "Balança" },
    { key: "docas", label: "Agendamento Doca / Op. Logística" },
    { key: "envase", label: "Recebimento / Linha de Envase" },
    { key: "triagem", label: "Contentores Vazios / Manutenção" },
    { key: "armazenagem", label: "Estoque Cheios / Expedição" },
    { key: "rastreamento", label: "Rastreamento GPS" },
  ];

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "operator",
      base: u.base || "",
      password: "",
      modules: u.modules || [],
    });
  };

  const toggleUserModule = (form, setForm, key) => {
    const cur = form.modules || [];
    setForm({ ...form, modules: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
  };

  // Só oferece os módulos que a BASE do usuário tem ligados (gestor só libera o que a base dele opera)
  const modulosDaBase = (baseId) => {
    const b = bases.find((x) => x.id === baseId || x.code === baseId);
    if (!b) return MODULOS_USUARIO;
    return MODULOS_USUARIO.filter((m) => (m.key === "balanca" ? b.modules?.balanca === true : b.modules?.[m.key] !== false));
  };

  const ModulosUsuario = ({ form, setForm, prefix }) => {
    const baseId = isAdmin ? form.base : currentUser?.base;
    const lista = modulosDaBase(baseId);
    return (
    <div className="border border-border bg-[#0A0A0C] p-3" data-testid={`${prefix}-modulos`}>
      <div className="text-[11px] uppercase tracking-wider text-[#FFD60A] font-bold">Onde vai atuar (módulos visíveis)</div>
      <div className="text-[10px] text-muted-foreground mb-2">Marque só onde a pessoa trabalha (ex.: só Portaria ou só Balança). Ela não verá as outras telas. Nenhum marcado = vê tudo que a base tem ligado.{lista.length < MODULOS_USUARIO.length && " Só aparecem os módulos ligados nesta base."}</div>
      {lista.length === 0 && <div className="text-xs text-[#FF9F0A]">Esta base não tem nenhum módulo ligado.</div>}
      <div className="grid grid-cols-2 gap-1.5">
        {lista.map((m) => {
          const on = (form.modules || []).includes(m.key);
          return (
            <button key={m.key} type="button" onClick={() => toggleUserModule(form, setForm, m.key)} data-testid={`${prefix}-mod-${m.key}`}
                    className={`flex items-center gap-2 border px-2 py-1.5 text-left text-xs transition-colors ${on ? "border-[#FFD60A] bg-[#FFD60A]/10 text-[#FFD60A]" : "border-border text-muted-foreground hover:text-white"}`}>
              <span className={`w-3.5 h-3.5 border flex items-center justify-center ${on ? "border-[#FFD60A] bg-[#FFD60A]" : "border-muted-foreground/50"}`}>
                {on && <CheckIcon className="w-3 h-3 text-black" strokeWidth={3} />}
              </span>
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
    );
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    const payload = {};
    if (editUserForm.name !== editingUser.name) payload.name = editUserForm.name;
    if (editUserForm.email.toLowerCase() !== (editingUser.email || "").toLowerCase()) payload.email = editUserForm.email;
    if (editUserForm.role !== editingUser.role) payload.role = editUserForm.role;
    if ((editUserForm.base || "") !== (editingUser.base || "")) payload.base = editUserForm.base;
    if (editUserForm.password && editUserForm.password.length >= 4) payload.password = editUserForm.password;
    const modsAntes = [...(editingUser.modules || [])].sort().join(",");
    const modsDepois = [...(editUserForm.modules || [])].sort().join(",");
    if (modsAntes !== modsDepois) {
      if (!editUserForm.modules?.length) payload.clear_modules = true; else payload.modules = editUserForm.modules;
    }
    if (!Object.keys(payload).length) { toast.info("Nada para salvar"); return; }
    try {
      const endpoint = isAdmin ? `/admin/users/${editingUser.id}` : `/manager/users/${editingUser.id}`;
      await api.patch(endpoint, payload);
      toast.success("Usuário atualizado" + (payload.password ? " · nova senha exigirá troca no próximo login" : ""));
      setEditingUser(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePortalCliente = async (c) => {
    try {
      const { data } = await api.patch(`/admin/clientes/${c.id}/portal`, { enabled: !c.portal_enabled });
      toast.success(`Portal do Cliente ${data.portal_enabled ? "LIGADO" : "DESLIGADO"} para ${c.nome}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copiarLinkCliente = async (c) => {
    if (!c.portal_token) { toast.error("Ligue o portal primeiro — o link é gerado automaticamente"); return; }
    const url = `${window.location.origin}/cliente/acesso/${c.portal_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link do portal de ${c.nome} copiado!`);
    } catch {
      window.prompt("Copie o link do Portal do Cliente:", url);
    }
  };

  const regenLinkCliente = async (c) => {
    if (!window.confirm(`Gerar NOVO link para ${c.nome}? O anterior deixará de funcionar.`)) return;
    try {
      await api.post(`/admin/clientes/${c.id}/portal-token`);
      toast.success("Novo link gerado — o antigo foi revogado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditCliente = (c) => {
    setEditingCliente(c);
    setEditClienteForm({
      nome: c.nome || "",
      cnpj: c.cnpj || "",
      cidade: c.cidade || "",
      estado: c.estado || "",
    });
  };

  const saveEditCliente = async () => {
    if (!editingCliente) return;
    const payload = {};
    ["nome", "cnpj", "cidade", "estado"].forEach((k) => {
      if ((editClienteForm[k] || "") !== (editingCliente[k] || "")) payload[k] = editClienteForm[k];
    });
    if (!Object.keys(payload).length) { toast.info("Nada para salvar"); return; }
    try {
      await api.patch(`/admin/clientes/${editingCliente.id}`, payload);
      toast.success(editingCliente.tipo === "wms" ? "Cliente WMS atualizado — base vinculada sincronizada" : "Cliente atualizado");
      setEditingCliente(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Excluir usuário?")) return;
    try {
      const endpoint = isAdmin ? `/admin/users/${id}` : `/manager/users/${id}`;
      await api.delete(endpoint);
      toast.success("Excluído");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlockUser = async (u) => {
    try {
      await api.post(`/manager/users/${u.id}/unlock`);
      toast.success(`${u.name} desbloqueado — pode tentar o login novamente`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createCliente = async () => { /* legacy noop - replaced by ClienteForm */ };

  const deleteCliente = async (id) => {
    if (!window.confirm("Inativar cliente?")) return;
    try {
      await api.delete(`/admin/clientes/${id}`);
      toast.success("Inativado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditBase = (b) => {
    setEditingBase(b);
    setEditForm({ name: b.name || "", code: b.code || "", region: b.region || "", razao_social: b.razao_social || "", cnpj: b.cnpj || "", endereco: b.endereco || "" });
  };

  const saveEditBase = async () => {
    try {
      await api.patch(`/admin/bases/${editingBase.id}`, editForm);
      toast.success("Base atualizada");
      setEditingBase(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleGestorEdit = async (baseId, value) => {
    try {
      await api.patch(`/admin/bases/${baseId}`, { gestor_can_edit_modules: value });
      toast.success(value ? "Gestor pode editar módulos" : "Apenas ADM pode editar módulos");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveProduct = async () => {
    if (!newProduct.codigo || !newProduct.nome || !newProduct.densidade) return toast.error("Preencha código, nome e densidade");
    const payload = {
      codigo: parseInt(newProduct.codigo, 10),
      nome: newProduct.nome.trim(),
      funcao_quimica: newProduct.funcao_quimica.trim() || newProduct.nome.trim(),
      densidade: parseFloat(newProduct.densidade),
    };
    try {
      if (editingProduct) {
        await api.patch(`/admin/products/${editingProduct.id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produto criado");
      }
      setNewProduct({ codigo: "", nome: "", funcao_quimica: "", densidade: "" });
      setEditingProduct(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEditProduct = (p) => {
    setEditingProduct(p);
    setNewProduct({ codigo: String(p.codigo), nome: p.nome, funcao_quimica: p.funcao_quimica, densidade: String(p.densidade) });
  };

  const cancelEditProduct = () => {
    setEditingProduct(null);
    setNewProduct({ codigo: "", nome: "", funcao_quimica: "", densidade: "" });
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Excluir produto?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Excluído");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openBaseOps = async (b) => {
    // AÇÃO PRIMÁRIA: Entra em modo Impersonation e navega para o Dashboard
    setActiveBase({ code: b.code, name: b.name });
    toast.success(`Escopo global ativado · ${b.name}. Todas as telas agora exibem apenas dados desta filial.`);
    navigate("/dashboard");
  };

  const showBaseOpsDetail = async (b) => {
    // AÇÃO SECUNDÁRIA: apenas mostra o modal com dados agregados (sem impersonar)
    setBaseOps(b);
    setBaseOpsData(null);
    try {
      const { data } = await api.get(`/admin/bases/${b.id}/summary`);
      setBaseOpsData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteBase = async (b) => {
    if (!window.confirm(`Excluir base ${b.name}? Só é possível se não houver contentores nem usuários vinculados.`)) return;
    try {
      await api.delete(`/admin/bases/${b.id}`);
      toast.success("Base excluída");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadBaseContract = async (baseId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Arquivo grande demais (máx 5MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/admin/bases/${baseId}/contrato`, {
          contract_filename: file.name,
          contract_data_url: reader.result,
        });
        toast.success("Contrato anexado à base");
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-1">{isAdmin ? "Restrito · Administrador" : "Gestor · Base Operacional"}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Painel ADM</h1>
          <p className="text-sm text-muted-foreground mt-2">{isAdmin ? "Filiais, feature toggles, gerenciamento de operadores e auditoria." : "Gerenciamento de operadores, clientes destino e produtos químicos da sua base."}</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/download?fmt=pdf&token=${localStorage.getItem("token") || ""}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const r = await api.get("/portfolio/download", { params: { fmt: "pdf" }, responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a"); a.href = url; a.download = "chemwms_portfolio.pdf"; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error("Portfólio não disponível"); }
                }}
                data-testid="dl-portfolio-pdf"
                className="inline-flex items-center gap-2 h-10 px-4 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs uppercase tracking-wider font-bold"
              >
                <FileTextIcon className="w-4 h-4" /> Portfólio PDF
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/download?fmt=pptx`}
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const r = await api.get("/portfolio/download", { params: { fmt: "pptx" }, responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a"); a.href = url; a.download = "chemwms_portfolio.pptx"; a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { toast.error("Portfólio não disponível"); }
                }}
                data-testid="dl-portfolio-pptx"
                className="inline-flex items-center gap-2 h-10 px-4 border border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 text-xs uppercase tracking-wider font-bold"
              >
                <PresentationIcon className="w-4 h-4" /> Portfólio PPT
              </a>
            </div>
          )}
          <ShieldIcon className="w-12 h-12 text-[#FF3B30]" strokeWidth={1.2} />
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#14151A] rounded-none border border-border h-12 p-0">
          {isAdmin && (
            <TabsTrigger value="bases" data-testid="adm-tab-bases" className="rounded-none data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
              <BuildingIcon className="w-4 h-4 mr-2" /> Bases & Módulos
            </TabsTrigger>
          )}
          <TabsTrigger value="users" data-testid="adm-tab-users" className="rounded-none data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <UserPlusIcon className="w-4 h-4 mr-2" /> Operadores
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="audit" data-testid="adm-tab-audit" className="rounded-none data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white h-full px-6 font-bold uppercase tracking-wider text-xs">
              Auditoria
            </TabsTrigger>
          )}
          <TabsTrigger value="clientes" data-testid="adm-tab-clientes" className="rounded-none data-[state=active]:bg-[#34C759] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <BriefcaseIcon className="w-4 h-4 mr-2" /> Clientes Destino
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="clientes_wms" data-testid="adm-tab-clientes-wms" className="rounded-none data-[state=active]:bg-[#FFD60A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
              <BriefcaseIcon className="w-4 h-4 mr-2" /> Clientes WMS
            </TabsTrigger>
          )}
          <TabsTrigger value="produtos" data-testid="adm-tab-produtos" className="rounded-none data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <FlaskConicalIcon className="w-4 h-4 mr-2" /> Produtos Químicos
          </TabsTrigger>
          <TabsTrigger value="fornecedores" data-testid="adm-tab-fornecedores" className="rounded-none data-[state=active]:bg-[#FF9F0A] data-[state=active]:text-black h-full px-6 font-bold uppercase tracking-wider text-xs">
            <TruckIcon className="w-4 h-4 mr-2" /> Fornecedores
          </TabsTrigger>
        </TabsList>

        {/* FORNECEDORES + DOCAS */}
        <TabsContent value="fornecedores" className="mt-4">
          <FornecedoresAdmin isAdmin={isAdmin} />
        </TabsContent>

        {/* BASES */}
        <TabsContent value="bases" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bases.map((b) => {
              const inactive = b.active === false;
              return (
              <Card key={b.id} data-testid={`base-${b.id}`} className={`bg-[#14151A] border-border rounded-none p-5 ${inactive ? "opacity-60 border-l-2 border-l-[#FF3B30]" : ""}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-display text-2xl font-bold">{b.name}</div>
                      {inactive && <Badge className="rounded-none border bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40 font-bold text-[10px]">INATIVA</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => openEditBase(b)} data-testid={`edit-base-${b.id}`} className="h-6 w-6 p-0 text-muted-foreground hover:text-[#FFD60A]">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">{b.code}{b.region && ` · ${b.region}`}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{inactive ? "Inativa" : "Ativa"}</span>
                    <Switch
                      checked={!inactive}
                      onCheckedChange={(v) => toggleActive(b.id, v)}
                      data-testid={`active-${b.id}`}
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { key: "envase", label: "Linha de Envase" },
                    { key: "triagem", label: "Triagem / Manutenção" },
                    { key: "armazenagem", label: "Armazenagem & Expedição" },
                    { key: "portaria", label: "Portaria & Controle de Acesso" },
                    { key: "balanca", label: "Balança · Controle de Veículos", optIn: true },
                    { key: "docas", label: "Agendamento Doca & Op. Logística" },
                    { key: "rastreamento", label: "Rastreamento GPS" },
                  ].map(({ key, label, optIn }) => (
                    <div key={key} className="flex items-center justify-between border border-border bg-[#0A0A0C] p-3">
                      <span className={`text-sm ${inactive ? "text-muted-foreground" : ""}`}>{label}</span>
                      <Switch
                        checked={(optIn ? b.modules?.[key] === true : b.modules?.[key] !== false) && !inactive}
                        onCheckedChange={(v) => toggleModule(b.id, key, v)}
                        disabled={inactive}
                        data-testid={`toggle-${b.id}-${key}`}
                      />
                    </div>
                  ))}
                </div>
                {inactive && (
                  <div className="mt-3 text-[11px] text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-2">
                    🔒 Base inativa: usuários desta filial não conseguem fazer login e ela some do dropdown de cadastro.
                  </div>
                )}
                {!inactive && !b.modules?.envase && (
                  <div className="mt-3 text-[11px] text-[#FF9F0A] border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-2">
                    ⚠ Envase desligado: ativos vão direto p/ armazenagem após triagem.
                  </div>
                )}

                {/* Toggle: gestor da base pode editar módulos */}
                <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between" data-testid={`gestor-toggle-row-${b.id}`}>
                  <div>
                    <div className="text-xs">Permitir que o Gestor da Base edite módulos</div>
                    <div className="text-[10px] text-muted-foreground">Quando ativo, o Gestor desta base pode ligar/desligar os módulos locais.</div>
                  </div>
                  <Switch
                    checked={!!b.gestor_can_edit_modules}
                    onCheckedChange={(v) => toggleGestorEdit(b.id, v)}
                    disabled={inactive}
                    data-testid={`gestor-edit-${b.id}`}
                  />
                </div>

                <div className="mt-3 pt-3 border-t border-border/60 flex items-center gap-2 flex-wrap">
                  <Button size="sm" onClick={() => openBaseOps(b)} data-testid={`ops-base-${b.id}`} className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 h-8 font-bold uppercase tracking-wider text-[10px]">
                    <EyeIcon className="w-3 h-3 mr-1" /> Operações da Base
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => showBaseOpsDetail(b)} data-testid={`ops-detail-${b.id}`} className="rounded-none border-border h-8 font-bold uppercase tracking-wider text-[10px]">
                    Ver Resumo
                  </Button>
                  <label className="rounded-none border border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10 h-8 px-3 font-bold uppercase tracking-wider text-[10px] flex items-center gap-1 cursor-pointer">
                    <PaperclipIcon className="w-3 h-3" />
                    {b.contract_filename ? "Trocar Contrato" : "Anexar Contrato"}
                    <input type="file" accept=".pdf,image/*" onChange={(e) => uploadBaseContract(b.id, e.target.files[0])} className="hidden" data-testid={`contract-${b.id}`} />
                  </label>
                  {b.contract_data_url && b.contract_filename && (
                    <a href={b.contract_data_url} download={b.contract_filename} className="text-[10px] text-[#00E5FF] hover:underline font-mono truncate max-w-[140px]">{b.contract_filename}</a>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => deleteBase(b)} data-testid={`del-base-${b.id}`} className="ml-auto h-8 text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold uppercase tracking-wider text-[10px]">
                    <Trash2Icon className="w-3 h-3 mr-1" /> Excluir
                  </Button>
                </div>
              </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4">
          <Card className="bg-[#14151A] border-border rounded-none p-5 mb-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-3">Cadastrar novo operador</div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome</Label>
                <Input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Login (e-mail)</Label>
                <Input data-testid="new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Senha</Label>
                <Input data-testid="new-user-pw" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Perfil</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="new-user-role" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14151A] border-border rounded-none">
                    <SelectItem value="operator">Operação</SelectItem>
                    <SelectItem value="manager">Gestor</SelectItem>
                    {isAdmin && <SelectItem value="admin">Administrador</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Base</Label>
                  <Select value={newUser.base} onValueChange={(v) => setNewUser({ ...newUser, base: v })}>
                    <SelectTrigger data-testid="new-user-base" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-[#14151A] border-border rounded-none">
                      {bases.filter((b) => b.active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-end">
                <Button onClick={createUser} data-testid="new-user-submit" className="w-full h-10 rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs">
                  <PlusIcon className="w-4 h-4 mr-1" /> Cadastrar
                </Button>
              </div>
            </div>
            {newUser.role !== "admin" && <div className="mt-3"><ModulosUsuario form={newUser} setForm={setNewUser} prefix="new-user" /></div>}
          </Card>

          <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">E-mail</th>
                  <th className="text-left px-4 py-3">Perfil</th>
                  <th className="text-left px-4 py-3">Base</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-border/60" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-none border ${u.role === "admin" ? "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40" : u.role === "manager" ? "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40" : "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40"}`}>{u.role}</Badge>
                      {u.locked && (
                        <Badge data-testid={`locked-${u.email}`} className="rounded-none border bg-[#FF3B30] text-white border-[#FF3B30] ml-1 text-[9px] font-black">BLOQUEADO</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{u.base || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {u.locked && (
                        <Button size="sm" variant="outline" onClick={() => unlockUser(u)} data-testid={`unlock-user-${u.email}`}
                                className="h-7 px-2 mr-2 rounded-none border-[#34C759] text-[#34C759] hover:bg-[#34C759]/10 text-[10px] font-bold uppercase">
                          Desbloquear
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.email}`} className="h-7 w-7 p-0 mr-1 hover:text-[#FFD60A]">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} data-testid={`del-user-${u.email}`} className="h-7 w-7 p-0 hover:text-[#FF3B30]">
                        <Trash2Icon className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* AUDIT */}
        <TabsContent value="audit" className="mt-4">
          <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Timestamp</th>
                  <th className="text-left px-4 py-3">Usuário</th>
                  <th className="text-left px-4 py-3">Ação</th>
                  <th className="text-left px-4 py-3">Alvo</th>
                  <th className="text-left px-4 py-3">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">Sem registros.</td></tr>}
                {auditLog.map((a) => (
                  <tr key={a.id} className="border-t border-border/60" data-testid={`audit-${a.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px]">{new Date(a.ts).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-2 text-xs">{a.user} <span className="text-[10px] text-muted-foreground">({a.role})</span></td>
                    <td className="px-4 py-2"><Badge className="rounded-none border bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40 font-mono text-[10px]">{a.action}</Badge></td>
                    <td className="px-4 py-2 font-mono text-xs text-[#FFD60A]">{a.target}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{a.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* CLIENTES DESTINO */}
        <TabsContent value="clientes" className="mt-4">
          <Card className="bg-[#14151A] border-border rounded-none p-5 mb-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#34C759] mb-3">Cadastrar Cliente de Destino (destinatário da carga)</div>
            <ClienteForm defaultTipo="destino" onCreated={() => { load(); toast.info("Cliente de destino cadastrado · disponível no dropdown de Expedição"); }} />
          </Card>

          <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Nome / Razão Social</th>
                  <th className="text-left px-4 py-3">CNPJ</th>
                  <th className="text-left px-4 py-3">Cidade / UF</th>
                  <th className="text-left px-4 py-3">Cadastrado por</th>
                  {isAdmin && <th className="text-left px-4 py-3">Contrato</th>}
                  {isAdmin && <th className="text-left px-4 py-3">Portal do Cliente</th>}
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {clientes.length === 0 && <tr><td colSpan={isAdmin ? "7" : "5"} className="text-center text-muted-foreground py-8">Nenhum cliente cadastrado.</td></tr>}
                {clientes.map((c) => (
                  <tr key={c.id} className="border-t border-border/60" data-testid={`cli-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium">{c.nome}</td>
                    <td className="px-4 py-3 font-mono text-xs">{c.cnpj}</td>
                    <td className="px-4 py-3 text-xs">{c.cidade}{c.estado && ` / ${c.estado}`}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{c.created_by}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-xs">
                        {c.contract_data_url ? (
                          <a href={c.contract_data_url} download={c.contract_filename} className="text-[#34C759] hover:underline flex items-center gap-1" data-testid={`cli-download-${c.id}`}>
                            <PaperclipIcon className="w-3 h-3" /> {c.contract_filename}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-center gap-2">
                          <Switch checked={!!c.portal_enabled} onCheckedChange={() => togglePortalCliente(c)} data-testid={`portal-switch-${c.id}`} />
                          {c.portal_enabled && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => copiarLinkCliente(c)} data-testid={`portal-link-${c.id}`} className="h-7 w-7 p-0 hover:text-[#34C759]" title="Copiar link do portal">
                                <LinkIcon className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => regenLinkCliente(c)} data-testid={`portal-regen-${c.id}`} className="h-7 w-7 p-0 hover:text-[#FFD60A]" title="Regenerar link (revoga o anterior)">
                                <RefreshCwIcon className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEditCliente(c)} data-testid={`edit-cli-${c.id}`} className="h-7 w-7 p-0 mr-1 hover:text-[#FFD60A]">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCliente(c.id)} data-testid={`del-cli-${c.id}`} className="h-7 w-7 p-0 hover:text-[#FF3B30]">
                        <Trash2Icon className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* CLIENTES WMS (contratantes do sistema · somente ADM) */}
        <TabsContent value="clientes_wms" className="mt-4">
          {!isAdmin ? (
            <Card className="bg-[#14151A] border-border rounded-none p-8 text-center text-muted-foreground">
              Acesso restrito ao Administrador Geral.
            </Card>
          ) : (
            <>
              <Card className="bg-[#14151A] border-border rounded-none p-5 mb-4">
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] mb-3">Cadastrar Cliente WMS (contratante do sistema)</div>
                <div className="text-xs text-muted-foreground mb-3">Empresas/bases que contratam nosso WMS. Este cadastro é separado dos destinatários da carga.</div>
                <ClienteForm defaultTipo="wms" onCreated={() => { load(); toast.info("Cliente WMS cadastrado"); }} />
              </Card>

              <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-3">Nome / Razão Social</th>
                      <th className="text-left px-4 py-3">CNPJ</th>
                      <th className="text-left px-4 py-3">Cidade / UF</th>
                      <th className="text-left px-4 py-3">Cadastrado por</th>
                      <th className="text-left px-4 py-3">Contrato</th>
                      <th className="text-right px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesWMS.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-8">Nenhum cliente WMS cadastrado.</td></tr>}
                    {clientesWMS.map((c) => (
                      <tr key={c.id} className="border-t border-border/60" data-testid={`cli-wms-row-${c.id}`}>
                        <td className="px-4 py-3 font-medium">{c.nome}</td>
                        <td className="px-4 py-3 font-mono text-xs">{c.cnpj}</td>
                        <td className="px-4 py-3 text-xs">{c.cidade}{c.estado && ` / ${c.estado}`}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">{c.created_by}</td>
                        <td className="px-4 py-3 text-xs">
                          {c.contract_data_url ? (
                            <a href={c.contract_data_url} download={c.contract_filename} className="text-[#34C759] hover:underline flex items-center gap-1">
                              <PaperclipIcon className="w-3 h-3" /> {c.contract_filename}
                            </a>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => openEditCliente(c)} data-testid={`edit-cli-wms-${c.id}`} className="h-7 w-7 p-0 mr-1 hover:text-[#FFD60A]">
                            <EditIcon className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteCliente(c.id)} className="h-7 w-7 p-0 hover:text-[#FF3B30]">
                            <Trash2Icon className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </TabsContent>
        {/* PRODUTOS QUÍMICOS */}
        <TabsContent value="produtos" className="mt-4">
          <Card className="bg-[#14151A] border-border rounded-none p-5 mb-4">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-3">
              {editingProduct ? `Editar produto · Cód. ${editingProduct.codigo}` : "Cadastrar produto químico"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Código</Label>
                <Input data-testid="new-prod-codigo" type="number" value={newProduct.codigo} onChange={(e) => setNewProduct({ ...newProduct, codigo: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" disabled={!!editingProduct} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome comercial</Label>
                <Input data-testid="new-prod-nome" value={newProduct.nome} onChange={(e) => setNewProduct({ ...newProduct, nome: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="Ex: SISBRAX ACE 75 CNT 5000L" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Função Química</Label>
                <Input data-testid="new-prod-funcao" value={newProduct.funcao_quimica} onChange={(e) => setNewProduct({ ...newProduct, funcao_quimica: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="Ex: ÁCIDO ACÉTICO 75%" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Densidade (kg/L)</Label>
                <Input data-testid="new-prod-densidade" type="number" step="0.001" value={newProduct.densidade} onChange={(e) => setNewProduct({ ...newProduct, densidade: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" placeholder="1.071" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveProduct} data-testid="prod-submit" className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider text-xs h-10 px-6">
                <PlusIcon className="w-4 h-4 mr-1" /> {editingProduct ? "Salvar alterações" : "Cadastrar produto"}
              </Button>
              {editingProduct && (
                <Button variant="outline" onClick={cancelEditProduct} className="rounded-none border-border h-10">Cancelar</Button>
              )}
            </div>
          </Card>

          <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Código</th>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Função Química</th>
                  <th className="text-right px-4 py-3">Densidade</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-8">Nenhum produto cadastrado.</td></tr>}
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-border/60" data-testid={`prod-row-${p.codigo}`}>
                    <td className="px-4 py-3 font-mono text-[#FFD60A] font-bold">{p.codigo}</td>
                    <td className="px-4 py-3 font-medium">{p.nome}</td>
                    <td className="px-4 py-3 text-xs text-[#00E5FF] font-mono">{p.funcao_quimica}</td>
                    <td className="px-4 py-3 text-right font-mono">{p.densidade}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => startEditProduct(p)} data-testid={`edit-prod-${p.codigo}`} className="h-7 w-7 p-0 mr-1 hover:text-[#FFD60A]">
                        <EditIcon className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteProduct(p.id)} data-testid={`del-prod-${p.codigo}`} className="h-7 w-7 p-0 hover:text-[#FF3B30]">
                        <Trash2Icon className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Base Operations Modal */}
      <Dialog open={!!baseOps} onOpenChange={(o) => !o && setBaseOps(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-2xl" data-testid="base-ops-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Operações da Base · {baseOps?.name}</DialogTitle>
            <div className="text-xs text-muted-foreground">Visão local de ativos, usuários e ações administrativas.</div>
          </DialogHeader>
          {!baseOpsData ? (
            <div className="text-xs text-muted-foreground animate-pulse">Carregando...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-border bg-[#0A0A0C] p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Contentores</div>
                  <div className="font-display text-3xl text-[#FFD60A] font-black">{baseOpsData.totals.containers}</div>
                </div>
                <div className="border border-border bg-[#0A0A0C] p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Usuários</div>
                  <div className="font-display text-3xl text-[#00E5FF] font-black">{baseOpsData.totals.users}</div>
                </div>
                <div className="border border-border bg-[#0A0A0C] p-3">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Status</div>
                  <div className={`font-display text-2xl font-black ${baseOpsData.base.active === false ? "text-[#FF3B30]" : "text-[#34C759]"}`}>{baseOpsData.base.active === false ? "INATIVA" : "ATIVA"}</div>
                </div>
              </div>
              <div className="border border-border bg-[#0A0A0C] p-3">
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-2">Ativos por Status</div>
                {Object.entries(baseOpsData.totals.by_status || {}).length === 0 && <div className="text-xs text-muted-foreground">Nenhum ativo vinculado a esta base.</div>}
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(baseOpsData.totals.by_status || {}).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between border border-border/50 px-3 py-2">
                      <span className="text-xs">{status.replace(/_/g, " ")}</span>
                      <span className="font-mono font-bold text-[#FFD60A]">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => { setBaseOps(null); openEditBase(baseOps); }} data-testid="ops-edit-base" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#FFD60A]/85 h-9 font-bold uppercase tracking-wider text-xs">
                  <EditIcon className="w-3 h-3 mr-1" /> Editar Base
                </Button>
                <Button size="sm" onClick={() => { setBaseOps(null); deleteBase(baseOps); }} data-testid="ops-delete-base" variant="outline" className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 h-9 font-bold uppercase tracking-wider text-xs">
                  <Trash2Icon className="w-3 h-3 mr-1" /> Excluir Base
                </Button>
                <a
                  href={`/estoque?base=${baseOps.code}`}
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-none border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 h-9 px-3 font-bold uppercase tracking-wider text-xs flex items-center"
                >
                  Ver Estoque desta Base
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Edit Base */}
      <Dialog open={!!editingBase} onOpenChange={(o) => !o && setEditingBase(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="edit-base-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Editar Dados da Base</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">Corrija erros de digitação. ID interno não muda.</div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nome exibido</Label>
              <Input data-testid="edit-base-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Sigla / Code</Label>
              <Input data-testid="edit-base-code" value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Região</Label>
              <Input data-testid="edit-base-region" value={editForm.region} onChange={(e) => setEditForm({ ...editForm, region: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="ex: Sudeste · São Paulo/SP" />
            </div>
            <div className="border-t border-border pt-3 space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFD60A]">Cabeçalho do ticket de pesagem (Balança)</div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Razão social</Label>
                <Input data-testid="edit-base-razao" value={editForm.razao_social} onChange={(e) => setEditForm({ ...editForm, razao_social: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="Nome da empresa no ticket" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">CNPJ</Label>
                  <Input data-testid="edit-base-cnpj" value={editForm.cnpj} onChange={(e) => setEditForm({ ...editForm, cnpj: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10 font-mono" placeholder="00.000.000/0000-00" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Endereço</Label>
                  <Input data-testid="edit-base-endereco" value={editForm.endereco} onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" placeholder="Rua, nº, cidade/UF" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingBase(null)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={saveEditBase} data-testid="edit-base-save" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Edit User */}
      <Dialog open={!!editingUser} onOpenChange={(o) => !o && setEditingUser(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <EditIcon className="w-5 h-5 text-[#FFD60A]" /> Editar Usuário
            </DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Ajuste nome, e-mail, perfil, base ou defina nova senha. Se preencher a senha, o usuário será obrigado a trocá-la no próximo login.
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nome</Label>
              <Input data-testid="edit-user-name" value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">E-mail</Label>
              <Input data-testid="edit-user-email" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Perfil</Label>
                <Select value={editUserForm.role} onValueChange={(v) => setEditUserForm({ ...editUserForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#14151A] border-border rounded-none">
                    <SelectItem value="operator">Operação</SelectItem>
                    <SelectItem value="manager">Gestor</SelectItem>
                    {isAdmin && <SelectItem value="admin">Administrador</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Base</Label>
                <Select value={editUserForm.base || "__none__"} onValueChange={(v) => setEditUserForm({ ...editUserForm, base: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-base" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#14151A] border-border rounded-none">
                    <SelectItem value="__none__">— sem base —</SelectItem>
                    {bases.filter((b) => b.active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nova Senha <span className="text-muted-foreground/70">(opcional · mín. 4 caracteres)</span></Label>
              <Input data-testid="edit-user-pw" type="password" value={editUserForm.password} onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" placeholder="Deixe vazio para manter" />
            </div>
            {editUserForm.role !== "admin" && <ModulosUsuario form={editUserForm} setForm={setEditUserForm} prefix="edit-user" />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={saveEditUser} data-testid="edit-user-save" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Edit Cliente */}
      <Dialog open={!!editingCliente} onOpenChange={(o) => !o && setEditingCliente(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="edit-cliente-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <EditIcon className="w-5 h-5 text-[#FFD60A]" /> Editar {editingCliente?.tipo === "wms" ? "Cliente WMS" : "Cliente de Destino"}
            </DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              {editingCliente?.tipo === "wms"
                ? "Alterações no nome/cidade sincronizam automaticamente a Base operacional vinculada."
                : "Cliente destinatário das cargas — aparece no dropdown de Expedição."}
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nome / Razão Social</Label>
              <Input data-testid="edit-cli-nome" value={editClienteForm.nome} onChange={(e) => setEditClienteForm({ ...editClienteForm, nome: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">CNPJ</Label>
              <Input data-testid="edit-cli-cnpj" value={editClienteForm.cnpj} onChange={(e) => setEditClienteForm({ ...editClienteForm, cnpj: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cidade</Label>
                <Input data-testid="edit-cli-cidade" value={editClienteForm.cidade} onChange={(e) => setEditClienteForm({ ...editClienteForm, cidade: e.target.value })} className="mt-1 rounded-none bg-[#0A0A0C] h-10" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">UF</Label>
                <Input data-testid="edit-cli-uf" maxLength={2} value={editClienteForm.estado} onChange={(e) => setEditClienteForm({ ...editClienteForm, estado: e.target.value.toUpperCase() })} className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCliente(null)} className="rounded-none border-border">Cancelar</Button>
            <Button onClick={saveEditCliente} data-testid="edit-cli-save" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
